import React from 'react'
import { XStack, YStack } from 'tamagui'
import { Trans } from '@lingui/react/macro'
import { useLingui } from '@lingui/react/macro'

import { Card } from '@newComponents/Card'
import { Icon } from '@newComponents/Icon'
import { Paragraph } from '@newComponents/Text'
import { Link } from '@newComponents/Link'

import { DocSection } from '../shared/DocSection'
import { DocScreenshot } from '../shared/DocScreenshot'

const Settings = () => {
  const { t } = useLingui()

  return (
    <>
      <Card gap='$4'>
        <Paragraph size='$5'>
          <Trans>
            Guild Settings let Organizers manage every aspect of their Guild — from
            basic details like the name and description to advanced features like
            monetization and integrations. Only Members with the Organizer role can
            access Settings.
          </Trans>
        </Paragraph>
        <Paragraph size='$5'>
          <Trans>
            To open Settings, navigate to your Guild's profile page and click the
            settings gear icon. This icon is only visible to Organizers.
          </Trans>
        </Paragraph>
      </Card>

      <Card gap='$4'>
        <DocSection id='general-settings' title={t`General Settings`}>
          <DocScreenshot
            src='docs/guilds/guild-settings-general.png'
            alt={t`Guild General Settings page showing fields for name, short name (slug), description, and timezone`}
            caption={t`General Settings let you manage your Guild name, URL, description, and timezone`}
          />
          <Paragraph size='$5'>
            <Trans>
              The General tab is where you manage the core identity of your Guild:
            </Trans>
          </Paragraph>
          <YStack gap='$3'>
            <XStack gap='$3' alignItems='flex-start'>
              <Icon name='edit' size='$5' color='$color' flexShrink={0} marginTop={2} />
              <Paragraph size='$5'>
                <Trans>
                  <Paragraph size='$5' fontWeight='$7'>Name</Paragraph> — Your Guild's
                  display name, shown on the profile page, in search results, and on
                  Event cards.
                </Trans>
              </Paragraph>
            </XStack>
            <XStack gap='$3' alignItems='flex-start'>
              <Icon name='link' size='$5' color='$color' flexShrink={0} marginTop={2} />
              <Paragraph size='$5'>
                <Trans>
                  <Paragraph size='$5' fontWeight='$7'>Short name (slug)</Paragraph> —
                  The URL-friendly identifier for your Guild. A Guild with the short name
                  "metro-coders" is available at guild.host/metro-coders. Choose something
                  short and memorable. Changing this will update your Guild's URL, so
                  share the new link with your community if you make changes.
                </Trans>
              </Paragraph>
            </XStack>
            <XStack gap='$3' alignItems='flex-start'>
              <Icon name='edit' size='$5' color='$color' flexShrink={0} marginTop={2} />
              <Paragraph size='$5'>
                <Trans>
                  <Paragraph size='$5' fontWeight='$7'>Description</Paragraph> — A rich
                  text description of your Guild that appears on the profile page. Use
                  this to explain what your community is about, who it's for, and what
                  Members can expect.
                </Trans>
              </Paragraph>
            </XStack>
            <XStack gap='$3' alignItems='flex-start'>
              <Icon name='globe' size='$5' color='$color' flexShrink={0} marginTop={2} />
              <Paragraph size='$5'>
                <Trans>
                  <Paragraph size='$5' fontWeight='$7'>Timezone</Paragraph> — The default
                  timezone for your Guild, used when displaying Event times. This is
                  especially helpful for regional communities where Events happen in a
                  consistent timezone.
                </Trans>
              </Paragraph>
            </XStack>
            <XStack gap='$3' alignItems='flex-start'>
              <Icon name='image' size='$5' color='$color' flexShrink={0} marginTop={2} />
              <Paragraph size='$5'>
                <Trans>
                  <Paragraph size='$5' fontWeight='$7'>Profile photo</Paragraph> — Your
                  Guild's logo or avatar. Appears in search results, Member lists, and
                  Event cards.
                </Trans>
              </Paragraph>
            </XStack>
            <XStack gap='$3' alignItems='flex-start'>
              <Icon name='image' size='$5' color='$color' flexShrink={0} marginTop={2} />
              <Paragraph size='$5'>
                <Trans>
                  <Paragraph size='$5' fontWeight='$7'>Cover photo</Paragraph> — A wide
                  banner image displayed at the top of your Guild's profile page. A good
                  cover photo sets the visual tone for your community.
                </Trans>
              </Paragraph>
            </XStack>
          </YStack>
        </DocSection>
      </Card>

      <Card gap='$4'>
        <DocSection id='other-settings' title={t`Other Settings`}>
          <Paragraph size='$5'>
            <Trans>
              Beyond General settings, Organizers have access to several additional
              configuration areas:
            </Trans>
          </Paragraph>
          <YStack gap='$3'>
            <XStack gap='$3' alignItems='flex-start'>
              <Icon name='people' size='$5' color='$color' flexShrink={0} marginTop={2} />
              <Paragraph size='$5'>
                <Trans>
                  <Paragraph size='$5' fontWeight='$7'>
                    <Link href='/docs/guilds/members'>Members</Link>
                  </Paragraph>{' '}
                  — Manage your Guild's membership, promote or demote roles, remove
                  Members, export member data, and configure{' '}
                  <Link href='/docs/guilds/forms'>Membership Forms</Link> for approval-based
                  joining.
                </Trans>
              </Paragraph>
            </XStack>
            <XStack gap='$3' alignItems='flex-start'>
              <Icon name='money' size='$5' color='$color' flexShrink={0} marginTop={2} />
              <Paragraph size='$5'>
                <Trans>
                  <Paragraph size='$5' fontWeight='$7'>
                    <Link href='/docs/guilds/monetization'>Monetization</Link>
                  </Paragraph>{' '}
                  — Connect Stripe to accept payments for Event tickets and donations.
                  Configure payout settings and manage your Guild's financial setup.
                </Trans>
              </Paragraph>
            </XStack>
            <XStack gap='$3' alignItems='flex-start'>
              <Icon name='link' size='$5' color='$color' flexShrink={0} marginTop={2} />
              <Paragraph size='$5'>
                <Trans>
                  <Paragraph size='$5' fontWeight='$7'>Integrations</Paragraph> — Connect
                  your Guild to third-party services including{' '}
                  <Link href='/docs/guilds/bluesky'>Bluesky</Link> for automatic Event
                  announcements,{' '}
                  <Link href='/docs/guilds/google-calendar'>Google Calendar</Link> for
                  Event syncing, and{' '}
                  <Link href='/docs/guilds/embeds'>Embeds</Link> for displaying your
                  Guild's Events on external websites.
                </Trans>
              </Paragraph>
            </XStack>
            <XStack gap='$3' alignItems='flex-start'>
              <Icon name='mail' size='$5' color='$color' flexShrink={0} marginTop={2} />
              <Paragraph size='$5'>
                <Trans>
                  <Paragraph size='$5' fontWeight='$7'>
                    <Link href='/docs/guilds/messaging'>Messaging</Link>
                  </Paragraph>{' '}
                  — Send messages and announcements to your Guild's Members.
                </Trans>
              </Paragraph>
            </XStack>
          </YStack>
        </DocSection>
      </Card>
    </>
  )
}

export default Settings
